/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useEffect, useState } from "react";

// react-router-dom components

// @mui material components

// @mui icons

// Material Dashboard 2 React components

// Authentication layout components

// Images
import { Visibility, VisibilityOff } from "@mui/icons-material";
import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  Typography,
  useTheme,
} from "@mui/material";
import { App } from "antd";
import { useFormik } from "formik";
import { Link, useNavigate } from "react-router-dom";

import signInImage from "~/assets/images/signInImage.png";
import brandLogo from "~/assets/images/team-logo.png";
import breakpoints from "~/assets/theme/base/breakpoints";

// Material Dashboard 2 React base styles
import { useMutation } from "@tanstack/react-query";
import * as Yup from "yup";
import MDBox from "~/components/MDBox";
import Footer from "~/examples/Footer";
import PageLayout from "~/examples/LayoutContainers/PageLayout";
import authService from "~/services/auth.service";

function Basic() {
  const [mobileView, setMobileView] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);

  const navigate = useNavigate();
  const handleSetRememberMe = () => setRememberMe(!rememberMe);
  const [showPassword, setShowPassword] = useState(false);
  const { message } = App.useApp();

  const { mutateAsync, isLoading } = useMutation({
    mutationFn: (payloads) => authService.login(payloads),
  });
  const theme = useTheme();
  const formik = useFormik({
    initialValues: {
      email: "",
      password: "",
      submit: null,
    },
    validationSchema: Yup.object({
      email: Yup.string()
        .email("Must be a valid email")
        .max(255)
        .required("Email Id is required"),
      password: Yup.string().max(255).required("Password is required"),
    }),
    onSubmit: async (values, helpers) => {
      try {
        const { email, password } = values;
        const response = await mutateAsync({ email, password });

        message.success("Login Successful");
        navigate("/dashboard");

        // navigate(config.defaultPath, { replace: true });
      } catch (err) {
        message.error("Invalid username or password");
        console.log("<< Error >>", err);
        helpers.setStatus({ success: false });
        helpers.setErrors({ submit: "Invalid username or password" });
        helpers.setSubmitting(false);
      }
    },
  });

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  useEffect(() => {
    // A function that sets the display state for the DefaultNavbarMobile.
    function displayMobileNavbar() {
      if (window.innerWidth < breakpoints.values.lg) {
        setMobileView(true);
      } else {
        setMobileView(false);
      }
    }

    /** 
     The event listener that's calling the displayMobileNavbar function when 
     resizing the window.
    */
    window.addEventListener("resize", displayMobileNavbar);

    // Call the displayMobileNavbar function to set the state with the initial value.
    displayMobileNavbar();

    // Remove event listener on cleanup
    return () => window.removeEventListener("resize", displayMobileNavbar);
  }, []);

  return (
    <PageLayout>
      <Box
        sx={{ height: "100vh" }}
        display="flex"
        flexDirection={{ xs: "column", md: "row" }}
        justifyContent={mobileView ? "center" : "space-between"}
        alignItems="center"
        mt="-20px"
        pb={{ xs: "20px", md: "0px" }}
      >
        <Box
          sx={{
            width: { xs: "100%", md: "50%", lg: "50%" },
            height: "inherit",
          }}
        >
          <Grid
            height={"inherit"}
            container
            justifyContent="center"
            alignItems="flex-end"
          >
            <Grid item lg={6}>
              <Grid container direction="column">
                <Grid item>
                  <img
                    src={brandLogo}
                    alt="Team Computers"
                    style={{ height: "40px", width: "90px" }}
                  />
                </Grid>
                <Grid item>
                  <Typography
                    variant="h3"
                    component="div"
                    mb={4}
                    sx={{ color: "#22A699" }}
                  >
                    Welcome Back
                  </Typography>
                </Grid>
                <Grid item>
                  <Typography
                    variant="subtitle2"
                    color="textSecondary"
                    fontWeight="bold"
                    fontSize="14px"
                  >
                    Enter your email and password to sign in
                  </Typography>
                </Grid>
              </Grid>
              <MDBox
                component="form"
                role="form"
                noValidate
                onSubmit={formik.handleSubmit}
              >
                <Grid container spacing={2} direction="column">
                  <Grid item lg={4}>
                    <FormControl
                      margin="normal"
                      required
                      fullWidth
                      error={
                        formik.touched.email && Boolean(formik.errors.email)
                      }
                      sx={{ ...theme.typography.customInput }}
                      disabled={isLoading}
                    >
                      <InputLabel htmlFor="email">Email</InputLabel>
                      <OutlinedInput
                        id="email"
                        name="email"
                        autoComplete="email"
                        label="Email"
                        placeholder="Enter your email address"
                        {...formik.getFieldProps("email")}
                        error={
                          formik.touched.email && Boolean(formik.errors.email)
                        }
                      />
                      {formik.touched.email && formik.errors.email && (
                        <FormHelperText error>
                          {formik.errors.email}
                        </FormHelperText>
                      )}
                    </FormControl>
                  </Grid>
                  <Grid item lg={4}>
                    <FormControl
                      disabled={isLoading}
                      margin="normal"
                      required
                      fullWidth
                      error={
                        formik.touched.password &&
                        Boolean(formik.errors.password)
                      }
                      sx={{ ...theme.typography.customInput }}
                    >
                      <InputLabel htmlFor="password">Password</InputLabel>
                      <OutlinedInput
                        id="password"
                        name="password"
                        label="Password"
                        type={showPassword ? "text" : "password"}
                        autoComplete="current-password"
                        {...formik.getFieldProps("password")}
                        error={
                          formik.touched.password &&
                          Boolean(formik.errors.password)
                        }
                        endAdornment={
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="toggle password visibility"
                              onClick={handleClickShowPassword}
                              onMouseDown={handleMouseDownPassword}
                            >
                              {showPassword ? (
                                <Visibility />
                              ) : (
                                <VisibilityOff />
                              )}
                            </IconButton>
                          </InputAdornment>
                        }
                      />
                      {formik.touched.password && formik.errors.password && (
                        <FormHelperText error>
                          {formik.errors.password}
                        </FormHelperText>
                      )}
                    </FormControl>
                  </Grid>
                  <Grid item>
                    <Button
                      disabled={isLoading}
                      type="submit"
                      fullWidth
                      variant="contained"
                      color="inherit"
                      size="large"
                      mt={3}
                      sx={{
                        borderRadius: "16px",
                        bgcolor: "#22A699",
                        color: "#f7f7f7",
                        "&:hover": {
                          backgroundColor: "#1B9C85",
                          color: "InfoText",
                        },
                      }}
                    >
                      {isLoading ? "Logging in..." : "Login"}
                    </Button>
                  </Grid>
                  <FormControl margin="normal" fullWidth>
                    <Link to="/authentication/forget-password">
                      <Button variant="text">Reset/Forgot Password?</Button>
                    </Link>
                  </FormControl>
                </Grid>
              </MDBox>
            </Grid>
            <Grid item>
              <Footer />
            </Grid>
          </Grid>
        </Box>
        <Box
          display={{ xs: "none", md: "block" }}
          overflowX="hidden"
          height="100%"
          width={{ md: "50%", lg: "50%" }}
          position="absolute"
          right="0px"
        >
          <Box
            style={{
              backgroundImage: `url(${signInImage})`,
              width: "100%",
              height: "100%",
              backgroundSize: "cover",
              backgroundPosition: "50%",
              position: "absolute",
              borderBottomLeftRadius: "20px",
            }}
          ></Box>
        </Box>
      </Box>
    </PageLayout>
  );
}

export default Basic;
